import {
  IDEMPOTENCY_HEADER,
  inboxCountsSchema,
  paginatedSchema,
  pendingDemandSchema,
  requisitionDetailSchema,
  requisitionLineSchema,
  requisitionSchema,
  requisitionTransitionResultSchema,
  requisitionWithLinesSchema,
  type CreateRequisitionInput,
  type DocLocale,
  type InboxCountsDto,
  type Paginated,
  type RequisitionDetailDto,
  type RequisitionDto,
  type RequisitionLineDto,
  type PendingDemandDto,
  type RequisitionLineInput,
  type RequisitionApprovalInput,
  type RequisitionPlacementInput,
  type RequisitionStatus,
  type RequisitionTransitionResultDto,
  type RequisitionWithLinesDto,
  type UpdateRequisitionInput,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Talabnoma endpointlari. Manba:
 *   `requisition/infrastructure/http/requisition.controller.ts`
 *   `requisition/infrastructure/http/transition.controller.ts`
 *
 * Javob sxemalari `@eombor/contracts` dan olinadi va bu yerda QAYTA
 * yozilmaydi. Eng muhim natija — `availableTransitions`: qaysi tugma
 * chizilishini SERVER aytadi, mijoz o'tish jadvalining nusxasini saqlamaydi.
 */

/*
 * `passthrough` — sxemada YO'Q maydonlar ham saqlanadi. Zod ularni odatda
 * jimgina kesib tashlaydi, shunda server yozuvchining stansiyasi va
 * korxonasini qaytara boshlasa ham kartochkaga yetib bormasdi
 * (`model/requester-origin.ts`).
 */
const requisitionPageSchema = paginatedSchema(requisitionSchema.passthrough());

/**
 * Talabnoma yaratish tanasi — SIM ustidagi shakl.
 *
 * `neededBy` sxemada `Date` (`z.coerce.date()` chiqishi), JSON da esa baribir
 * ISO satr bo'lib ketadi (`stock.api.ts` dagi `receivedAt` bilan bir xil
 * sabab): tur bilan haqiqat ajralib qolmasligi kerak.
 */
export interface CreateRequisitionBody {
  warehouseId: string;
  purpose: string;
  /** ISO sana yoki sana-vaqt. Berilmasa — muddat belgilanmagan. */
  neededBy?: string;
  note?: string;
  lines: CreateRequisitionInput['lines'];
}

export interface UpdateRequisitionBody {
  purpose?: string;
  neededBy?: string | null;
  note?: string | null;
}

export interface RequisitionQuery {
  status?: RequisitionStatus;
  warehouseId?: string;
  /** `true` — faqat o'zim yozgan talabnomalar (`?mine=true`). */
  mine?: boolean;
  page?: number;
  limit?: number;
}

/** `POST /requisitions/:id/transition` tanasi — `transitionRequisitionSchema` mijoz tomoni. */
export interface TransitionBody {
  to: RequisitionStatus;
  comment?: string;
  reason?: string;
  /**
   * Sklad qabul qilishda satrga biriktiriladigan MANZIL — partiya, katak va
   * tasdiqlangan miqdor.
   *
   * O'tish bilan BIRGA yuboriladi, alohida so'rov bilan emas: qoldiqni band
   * qilish ham, holatni o'zgartirish ham bitta tranzaksiyada bo'lishi kerak.
   * Ikki so'rovga bo'linsa, birinchisi o'tib ikkinchisi yiqilgan paytda
   * talabnoma "manzili bor, lekin qabul qilinmagan" holatda qolib ketardi.
   *
   * Boshqa o'tishlarda BERILMAYDI — server uni faqat `reserveStock` effekti
   * bor qatorda qabul qiladi.
   */
  lines?: RequisitionPlacementInput[];
  /**
   * KORXONA tasdiqlagan miqdorlar — faqat `SUBMITTED → ORG_APPROVED` da.
   *
   * `lines` dan ALOHIDA maydon, chunki ularni boshqa-boshqa odamlar,
   * boshqa-boshqa qadamlarda to'ldiradi: bu boshliqning qarori, `lines`
   * esa skladchining manzili. Bitta maydonga birlashtirilsa, "kim nimani
   * hal qildi" degan chegara yo'qolardi.
   */
  approvals?: RequisitionApprovalInput[];
}

export interface RequisitionApi {
  list: (query?: RequisitionQuery) => Promise<Paginated<RequisitionDto>>;
  /** "Mening navbatim" — ruxsat talab qilmaydi, ro'yxatning o'zi ruxsatdan hisoblanadi. */
  inbox: (page?: number, limit?: number) => Promise<Paginated<RequisitionDto>>;
  /**
   * Navbatning HOLAT BO'YICHA sanog'i — yon paneldagi belgilar uchun.
   *
   * Ro'yxat sahifasidan hisoblab bo'lmaydi: u 50 ta satr qaytaradi, navbat
   * esa undan uzun bo'lishi mumkin va belgi jimgina yolg'on gapirardi.
   */
  inboxCounts: () => Promise<InboxCountsDto>;
  /**
   * Xodimning O'Z bo'linmasi yozgan talabnomalar — «Mening omborim» tarixi.
   *
   * `status` berilsa, javobdagi `total` FAQAT o'sha holatdagilarniki bo'ladi —
   * «qabul qilinmagan nechta?» degan savol shu bilan javob topadi.
   */
  mineSubdivision: (
    page?: number,
    limit?: number,
    status?: RequisitionStatus,
  ) => Promise<Paginated<RequisitionDto>>;
  /**
   * KUTILAYOTGAN TALAB — hali band qilinmagan, lekin so'ralgan miqdor.
   *
   * Qoldiq javobiga QO'SHILMAYDI va bu ataylab: "nima bor" — sklad
   * konteksti, "nima so'ralgan" — talabnoma konteksti. Bitta javobga
   * siqilsa, ikki kontekst bir-biriga bog'lanib qolardi. Ekran ikkovini
   * o'zi birlashtiradi.
   */
  demand: (filter?: {
    warehouseId?: string;
    nomenclatureIds?: readonly string[];
    /** Shu talabnomaning O'ZI sanoqdan chiqariladi. */
    excludeRequisitionId?: string;
  }) => Promise<PendingDemandDto[]>;
  byId: (id: string) => Promise<RequisitionDetailDto>;
  /**
   * Yaratish va tahrirlash javobida `availableTransitions` YO'Q — u faqat
   * kartochka va o'tish javoblarida keladi. Shuning uchun turi ham boshqa:
   * bo'lmagan maydonni "bor" deb ko'rsatish yolg'on shartnoma bo'lardi.
   */
  create: (body: CreateRequisitionBody, idempotencyKey: string) => Promise<RequisitionWithLinesDto>;
  update: (id: string, body: UpdateRequisitionBody) => Promise<RequisitionWithLinesDto>;
  addLine: (id: string, line: RequisitionLineInput) => Promise<RequisitionLineDto>;
  removeLine: (id: string, lineId: string) => Promise<void>;
  transition: (
    id: string,
    body: TransitionBody,
    idempotencyKey: string,
  ) => Promise<RequisitionTransitionResultDto>;
  /**
   * TREBOVANIYA QORALAMASI — so'rovnoma ochilishidan oldingi ko'rinish.
   *
   * `accountingApi.requirementPdf` dan farqi bitta: bu yerda hujjat hali
   * YO'Q va blank ustida "QORALAMA" turadi.
   */
  requirementPdf: (id: string, locale: DocLocale) => Promise<Blob>;
}

/** Bo'sh qiymat so'rovga QO'SHILMAYDI — server uni baribir tekshiruvga tashlaydi. */
const listQueryString = (query: RequisitionQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 20),
  });
  if (query.status !== undefined) params.set('status', query.status);
  if (query.warehouseId !== undefined && query.warehouseId !== '') {
    params.set('warehouseId', query.warehouseId);
  }
  // Sxemada `'true' | 'false'` SATR — boshqa qiymat "hammasi" degani.
  if (query.mine === true) params.set('mine', 'true');
  return params.toString();
};

const path = (id: string): string => `/requisitions/${encodeURIComponent(id)}`;

/**
 * `CreateRequisitionInput` emas, o'z turimiz: kirish sxemasi `Date` kutadi,
 * tarmoqqa esa ISO satr ketadi. `UpdateRequisitionInput` bilan ham xuddi shu
 * sabab — ikkalasi shu yerda faqat hujjat sifatida eslatiladi.
 */
export type { CreateRequisitionInput, UpdateRequisitionInput };

export const createRequisitionApi = (client: ApiClient): RequisitionApi => ({
  list: (query = {}) =>
    client.apiFetch(`/requisitions?${listQueryString(query)}`, { schema: requisitionPageSchema }),

  inbox: (page = 1, limit = 20) =>
    client.apiFetch(`/requisitions/inbox?page=${page}&limit=${limit}`, {
      schema: requisitionPageSchema,
    }),

  inboxCounts: () =>
    client.apiFetch('/requisitions/inbox/counts', { schema: inboxCountsSchema }),

  mineSubdivision: (page = 1, limit = 20, status) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status !== undefined) params.set('status', status);
    return client.apiFetch(`/requisitions/subdivision/mine?${params.toString()}`, {
      schema: requisitionPageSchema,
    });
  },

  demand: (filter = {}) => {
    const params = new URLSearchParams();
    if (filter.warehouseId !== undefined) params.set('warehouseId', filter.warehouseId);
    /* Faqat EKRANDA ko'ringan mahsulotlar so'raladi: butun ombor talabini
       tortib olish ko'rinmaydigan mingta qatorni behuda olib kelardi. */
    if (filter.nomenclatureIds !== undefined && filter.nomenclatureIds.length > 0) {
      params.set('nomenclatureIds', filter.nomenclatureIds.join(','));
    }
    if (filter.excludeRequisitionId !== undefined) {
      params.set('excludeRequisitionId', filter.excludeRequisitionId);
    }
    return client.apiFetch(`/requisitions/demand?${params.toString()}`, {
      schema: pendingDemandSchema.array(),
    });
  },

  byId: (id) => client.apiFetch(path(id), { schema: requisitionDetailSchema }),

  /* `apiBlob` bilan, imzolangan havola bilan EMAS: `<a href>` ochilganda
     `Authorization` sarlavhasi ketmaydi va tokenni manzilga yozishga
     to'g'ri kelardi — u esa tarixda va jurnalda qolib ketardi. */
  requirementPdf: (id, locale) =>
    client.apiBlob(`${path(id)}/requirement.pdf?locale=${encodeURIComponent(locale)}`),

  create: (body, idempotencyKey) =>
    client.apiFetch('/requisitions', {
      method: 'POST',
      headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
      body: JSON.stringify(body),
      schema: requisitionWithLinesSchema,
    }),

  update: (id, body) =>
    client.apiFetch(path(id), {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: requisitionWithLinesSchema,
    }),

  addLine: (id, line) =>
    client.apiFetch(`${path(id)}/lines`, {
      method: 'POST',
      body: JSON.stringify(line),
      schema: requisitionLineSchema,
    }),

  removeLine: async (id, lineId) => {
    await client.apiFetch(`${path(id)}/lines/${encodeURIComponent(lineId)}`, { method: 'DELETE' });
  },

  transition: (id, body, idempotencyKey) =>
    client.apiFetch(`${path(id)}/transition`, {
      method: 'POST',
      headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
      body: JSON.stringify(body),
      schema: requisitionTransitionResultSchema,
    }),
});

export const requisitionApi = createRequisitionApi(apiClient);
